import {
	Alert,
	Box,
	Icons,
	Stack,
	Text,
	joinAttributes,
	Spinner,
	Button,
	Badge,
	Tabs,
	TabsList,
	TabContent,
	Inline,
	TabItem,
	Grid,
	Col,
	Stars,
	Table,
} from "@sembark-travel/ui-base"
import { useSearch, Search } from "@sembark-travel/ui-list"
import { ButtonLink, Link, queryToSearch } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import useSWR from "swr"
import { ILocation } from "../Locations"
import { generatePath } from "../router-utils"
import {
	ITrip,
	getBasicInfoForQueryFromTrip,
	IQuoteHotel,
	IItinerary,
	IQuoteTravelActivity,
	IQuoteCab,
	IQuoteTransportExtras,
	ITripQuote,
} from "./store"
import { useState, useCallback, useMemo } from "react"
import { TFormattedMoney } from "@sembark-travel/money"
import { ITransportService } from "../TransportServices"
import { TTourist } from "../Tourists"
import { ShareTripToDMCInDialog } from "./ShareTrip"
import {
	shouldAllowQuoteActions,
	tempDisableQuoteActionsMessageAlert,
} from "./utils"

interface IMatchingQuote {
	id: number
	trip_id: number
	name: string
	start_date: string
	start_date_local?: string
	days: string
	nights: number
	locations?: string
	price: TFormattedMoney
	no_of_adults: number
	no_of_children: number
	stay_nights: Array<{
		location: ILocation
		nights: number
	}>
	contact: TTourist
	inclusions: Array<string>
	hotel_categories: Array<string>
	grouped_daywise_services?: Array<{
		date: string
		date_local?: string
		services?: Array<{
			cab_schedule?: {
				cabs: Array<IQuoteCab>
				transport_service: ITransportService
			}
			travel_activity?: IQuoteTravelActivity
		}>
		extras?: Array<IQuoteTransportExtras>
	}>
	itinerary?: IItinerary
	remarks?: string
	used_cab_types?: Array<Pick<IQuoteCab, "cab_type" | "no_of_cabs">>
	trip_quote: ITripQuote
}

const formatCabDisplay = (
	cabs: Array<Pick<IQuoteCab, "cab_type" | "no_of_cabs">>,
	maxDisplay?: number
) => {
	if (!cabs.length) return ""
	if (maxDisplay) {
		if (cabs.length <= maxDisplay) {
			return cabs
				.map((cab) => `${cab.no_of_cabs}- ${cab.cab_type.name}`)
				.join(", ")
		}
		return `${cabs
			.slice(0, maxDisplay)
			.map((cab) => `${cab.no_of_cabs}- ${cab.cab_type.name}`)
			.join(", ")}...`
	}
	return cabs.map((cab) => `${cab.no_of_cabs}- ${cab.cab_type.name}`).join(", ")
}

const getServiceTitle = (
	service: {
		cab_schedule?: {
			cabs: Array<Pick<IQuoteCab, "cab_type" | "no_of_cabs">>
			transport_service: ITransportService
		}
		travel_activity?: IQuoteTravelActivity
	},
	usedCabTypes?: Array<Pick<IQuoteCab, "cab_type" | "no_of_cabs">>
): {
	type: "transport_service" | "travel_activity"
	title: string
	cabs?: Array<Pick<IQuoteCab, "cab_type" | "no_of_cabs">>
} | null => {
	if (service.cab_schedule) {
		return {
			type: "transport_service",
			title: service.cab_schedule.transport_service.name,
			cabs: usedCabTypes ? undefined : service.cab_schedule.cabs,
		}
	}
	if (service.travel_activity) {
		return {
			type: "travel_activity",
			title: `${service.travel_activity.activity.name} - ${service.travel_activity.ticket_type?.name || service.travel_activity.ticket_type?.short_name}`,
		}
	}
	return null
}

const ServiceBadge = ({
	type,
}: {
	type: "transport_service" | "travel_activity"
}) => (
	<Badge
		{...(type === "transport_service" ? { warning: true } : { success: true })}
	>
		{type === "transport_service" ? <Icons.Taxi /> : <Icons.Ticket />}
		{type === "transport_service" ? (
			<Box as="span" marginLeft={"1"}>
				Transport
			</Box>
		) : (
			<Box as="span" marginLeft={"1"}>
				Activity
			</Box>
		)}
	</Badge>
)

const ServiceTitle = ({
	title,
	cabs,
	maxCabs,
}: {
	title: string
	cabs?: Array<Pick<IQuoteCab, "cab_type" | "no_of_cabs">>
	maxCabs?: number
}) => (
	<Stack gap="1">
		<Text maxWidth="sm">{title}</Text>
		{cabs && cabs.length > 0 ? (
			<Text fontSize="xs" fontWeight="semibold">
				{formatCabDisplay(cabs, maxCabs)}
			</Text>
		) : null}
	</Stack>
)

export default function MatchingQuotes({
	trip,
	hasAIItineraryBuilder,
}: {
	trip: ITrip
	hasAIItineraryBuilder: boolean
}) {
	const xhr = useXHR()
	const [params, setParams] = useSearch({ q: "" })
	const { data: quotes, error } = useSWR<Array<IMatchingQuote>>(
		`/trips/${trip.id}/matching-quotes${queryToSearch(params)}`,
		() =>
			xhr
				.get(`/trips/${trip.id}/matching-quotes`, {
					params: {
						...params,
						limit: 4,
					},
				})
				.then((resp) => resp.data.data)
	)

	const isFetching = !quotes && !error
	if (!params.q && !quotes?.length) return null
	return (
		<Stack gap="4">
			<Box>
				<Search
					title={
						<Text fontWeight="semibold" fontSize="md">
							To Create Quote you can start with below suggestions.
						</Text>
					}
					initialParams={params}
					resetParams={{ q: "" }}
					onSearch={(params) => setParams(params)}
					placeholder="Search by trip id or guest name"
					actions={
						<Inline gap="2" alignItems="center">
							<ShareTripToDMCInDialog trip={trip}>
								{({ onClick }) => (
									<Button
										level="secondary"
										size="lg"
										data-testid="share_quote"
										onClick={onClick}
									>
										<Icons.Share /> Request Package
									</Button>
								)}
							</ShareTripToDMCInDialog>
							{hasAIItineraryBuilder ? (
								<ButtonLink
									to={generatePath("/trips/:tripId/ai-itinerary-builder", {
										tripId: trip.id.toString(),
									})}
									level="secondary"
									size="lg"
									bordered
									borderColor="transparent"
									borderBottomWidth={"1"}
								>
									<Icons.MagicStick size={"4"} /> Generate with AI
								</ButtonLink>
							) : null}
						</Inline>
					}
				/>
			</Box>
			{isFetching ? (
				<Spinner padding="4" alignCenter />
			) : quotes?.length ? (
				<QuoteSuggestionsViewAll matchingQuotes={quotes} trip={trip} />
			) : error ? (
				<Alert status="error">{error.message}</Alert>
			) : null}
		</Stack>
	)
}

export function QuoteSuggestionsViewAll({
	matchingQuotes,
	trip,
}: {
	matchingQuotes: Array<IMatchingQuote>
	trip: ITrip
}) {
	return (
		<Box>
			<Grid>
				{matchingQuotes.map((matchingQuote, index) => {
					return (
						<Col
							paddingY={"2"}
							paddingX={"2"}
							key={matchingQuote.id}
							xs={12}
							lg={6}
						>
							<Box>
								<QuoteSuggestionCard
									index={index}
									trip={trip}
									matchingQuote={matchingQuote}
								/>
							</Box>
						</Col>
					)
				})}
			</Grid>
		</Box>
	)
}

const HotelTable = ({
	hotels,
}: {
	hotels: Array<{
		hotel: {
			id: number
			name: string
			location?: {
				short_name?: string
			}
			stars?: string | number | null
		}
		nights: number[]
	}>
}) => (
	<Table
		headers={[
			<Text fontSize={"sm"}>Nights</Text>,
			<Text fontSize={"sm"}>Hotel</Text>,
			<Text fontSize={"sm"}>Location</Text>,
		]}
		rows={hotels.map((group) => [
			<Box key="nights" style={{ width: "110px" }}>
				<Badge
					primary
					style={{
						whiteSpace: "normal",
						fontVariant: "tabular-nums",
						textAlign: "left",
						lineHeight: "1.2",
					}}
				>
					{group.nights.length === 1
						? `${group.nights[0]}${group.nights[0] === 1 ? "st" : group.nights[0] === 2 ? "nd" : group.nights[0] === 3 ? "rd" : "th"} ${pluralize("Night", group.nights.length)}`
						: `${group.nights
								.map(
									(night: number) =>
										`${night}${night === 1 ? "st" : night === 2 ? "nd" : night === 3 ? "rd" : "th"}`
								)
								.join(", ")} ${pluralize("Night", group.nights.length)}`}
				</Badge>
			</Box>,
			<Box
				key="hotel"
				fontSize={"sm"}
				style={{
					textAlign: "left",
					width: "100%",
				}}
			>
				{joinAttributes(
					group.hotel.name,
					group.hotel?.stars ? (
						<Stars stars={group.hotel.stars} size="3" showSingleStar />
					) : null
				)}
			</Box>,
			<Inline
				key="location"
				gap="1"
				fontSize="xs"
				justifyContent={"end"}
				style={{ minWidth: "80px" }}
			>
				<Box
					style={{
						lineHeight: "1.3",
					}}
				>
					<Box as="span" marginRight={"1"}>
						<Icons.LocationMarker size={"4"} />
					</Box>

					{group.hotel?.location?.short_name
						? group.hotel.location.short_name
						: "N/A"}
				</Box>
			</Inline>,
		])}
		bordered
		striped
		hover
		alignCols={{
			2: "right",
		}}
	/>
)

function groupHotelsNightWise(hotels: Array<IQuoteHotel>) {
	return hotels.reduce(
		(
			acc: Array<{
				hotel: IQuoteHotel["hotel"]
				nights: number[]
			}>,
			hotel,
			index
		) => {
			const lastGroup = acc[acc.length - 1]
			if (lastGroup && lastGroup.hotel.id === hotel.hotel.id) {
				lastGroup.nights.push(index + 1)
			} else {
				acc.push({
					hotel: hotel.hotel,
					nights: [index + 1],
				})
			}
			return acc
		},
		[]
	)
}

function getDefaultTab(
	grouped_daywise_services: IMatchingQuote["grouped_daywise_services"],
	options: Array<ITripQuote>
) {
	if ((grouped_daywise_services || []).length) return "itinerary"
	if ((options || []).some((option) => (option.quote?.hotels || []).length))
		return "hotels"
	return "details"
}

export function QuoteSuggestionCard({
	index,
	trip,
	matchingQuote,
}: {
	index: number
	trip: ITrip
	matchingQuote: IMatchingQuote
}) {
	const {
		stay_nights,
		days,
		no_of_adults,
		no_of_children,
		grouped_daywise_services = [],
		itinerary: quoteItinerary,
		used_cab_types,
		trip_id,
		contact,
		trip_quote,
	} = matchingQuote

	const { pricing_strategy } = trip_quote

	const shouldAllowUseThisQuote = shouldAllowQuoteActions(pricing_strategy) //temp condition

	const [activeTab, setActiveTab] = useState<
		"itinerary" | "hotels" | "details"
	>(
		getDefaultTab(
			grouped_daywise_services,
			matchingQuote.trip_quote?.options || []
		)
	)
	const [expandedDays, setExpandedDays] = useState<string[]>([])
	const [showAllItinerary, setShowAllItinerary] = useState(false)
	const [expandedOptions, setExpandedOptions] = useState<
		Record<string, boolean>
	>({})
	const INITIAL_ROWS_LIMIT = 3
	const PRIMARY_ONLY_ROWS_LIMIT = 7

	const toggleDay = useCallback((day: string) => {
		setExpandedDays((prev) =>
			prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
		)
	}, [])

	const toggleOption = useCallback((optionId: string) => {
		setExpandedOptions((prev) => ({
			...prev,
			[optionId]: !prev[optionId],
		}))
	}, [])

	const totalPrimaryGroupedHotels = useMemo(() => {
		const hotels = matchingQuote.trip_quote.quote.hotels || []
		return hotels.length ? groupHotelsNightWise(hotels) : []
	}, [matchingQuote.trip_quote.quote.hotels])

	const groupedPrimaryHotels = useMemo(() => {
		const isPrimaryExpanded = expandedOptions["primary"]
		const hasAdditionalOptions =
			(matchingQuote.trip_quote.options || []).length > 0
		const limit = hasAdditionalOptions
			? INITIAL_ROWS_LIMIT
			: PRIMARY_ONLY_ROWS_LIMIT

		const groupedHotels = totalPrimaryGroupedHotels.length
			? totalPrimaryGroupedHotels
			: []
		return isPrimaryExpanded ? groupedHotels : groupedHotels.slice(0, limit)
	}, [
		expandedOptions,
		matchingQuote.trip_quote?.options,
		totalPrimaryGroupedHotels,
	])

	const additionalOptions = useMemo(() => {
		return (matchingQuote.trip_quote?.options || []).map((option) => {
			const optionHotels = option.quote.hotels || []
			const isExpanded = expandedOptions[option.id.toString()]

			const groupedHotels = optionHotels.length
				? groupHotelsNightWise(optionHotels)
				: []
			const displayedGroups = isExpanded
				? groupedHotels
				: groupedHotels.slice(0, INITIAL_ROWS_LIMIT)

			return {
				...option,
				groupedHotels: displayedGroups,
				totalGroupedHotels: groupedHotels,
			}
		})
	}, [matchingQuote.trip_quote?.options, expandedOptions])

	const getDescriptionFromItinerary = useCallback(
		(
			service: ITransportService | IQuoteTravelActivity,
			type: "transport_service" | "travel_activity",
			date: string
		) => {
			if (!quoteItinerary) return null

			const daySchedule = quoteItinerary.daywise_schedules.find(
				(schedule) =>
					schedule.start_date === date &&
					(type === "transport_service"
						? schedule.transport_service_id === service.id
						: schedule.travel_activity_id ===
								(service as IQuoteTravelActivity).activity.id &&
							schedule.travel_activity_ticket_type_id ===
								(service as IQuoteTravelActivity).ticket_type?.id)
			)

			return daySchedule ? daySchedule.description : null
		},
		[quoteItinerary]
	)

	const displayedServices =
		showAllItinerary && grouped_daywise_services
			? grouped_daywise_services
			: grouped_daywise_services
				? grouped_daywise_services.slice(0, 8)
				: []

	return (
		<Box
			bgColor={"default"}
			borderWidth="1"
			rounded="md"
			height="auto"
			overflow="hidden"
			marginY={"1"}
		>
			<Box paddingY="2" paddingX="4" borderBottomWidth="1">
				<Inline gap="4" justifyContent={"between"}>
					<Box>
						<Link
							fontSize="sm"
							color={"muted"}
							title="Click to view trip details"
							to={generatePath("/trips/:tripId", {
								tripId: matchingQuote.trip_id.toString(),
							})}
						>
							{joinAttributes(trip_id, contact.name)}
						</Link>
					</Box>
					<Stack gap="1">
						<Box>
							{stay_nights.length ? (
								<Inline
									flexWrap="wrap"
									gap="2"
									alignItems={"center"}
									justifyContent={"end"}
									fontWeight="semibold"
								>
									{stay_nights.map((s, i) => (
										<Inline
											key={i}
											alignItems="center"
											gap="2"
											title={`${pluralize("Night", s.nights, true)} at ${
												s.location.name
											}`}
											as="span"
										>
											{s.location.short_name}
											<Badge
												style={{
													fontVariant: "tabular-nums",
												}}
												status="primary"
											>
												{s.nights}N
											</Badge>
										</Inline>
									))}
								</Inline>
							) : null}
						</Box>
					</Stack>
				</Inline>
			</Box>

			<Box padding="2" paddingTop="0" height="full" overflow="auto">
				<Tabs layout="col">
					<TabsList>
						<TabItem
							id="itinerary"
							onClick={() => setActiveTab("itinerary")}
							active={activeTab === "itinerary"}
						>
							Itinerary
						</TabItem>
						<TabItem
							id="hotels"
							onClick={() => setActiveTab("hotels")}
							active={activeTab === "hotels"}
						>
							Hotels
						</TabItem>
						<TabItem
							id="details"
							onClick={() => setActiveTab("details")}
							active={activeTab === "details"}
						>
							Details
						</TabItem>
					</TabsList>

					<TabContent
						active={activeTab === "itinerary"}
						id="itinerary"
						key="itinerary"
						padding="2"
					>
						<Stack
							gap="2"
							style={{
								maxHeight: "420px",
								minHeight: "150px",
							}}
							overflow="auto"
						>
							{displayedServices.length ? (
								displayedServices.map((dayService, i) => {
									const titles = (dayService.services || []).map((item) => {
										const serviceTitle = getServiceTitle(item)
										return (
											serviceTitle || {
												type: "transport_service" as const,
												title: "",
											}
										)
									})

									return (
										<Stack gap="2" key={i} borderWidth="1">
											<Inline
												padding="1"
												gap="2"
												onClick={() => toggleDay(dayService.date)}
												justifyContent="between"
												alignItems="center"
												cursor="pointer"
												bgColor={
													expandedDays.includes(dayService.date)
														? "subtle"
														: "default"
												}
											>
												<Inline gap="2">
													<Box>
														<Badge
															style={{
																fontVariant: "tabular-nums",
															}}
															primary
														>
															Day {i + 1}
														</Badge>
													</Box>
													<Stack
														flex="1"
														gap="2"
														fontSize="sm"
														hidden={expandedDays.includes(dayService.date)}
													>
														{titles.map((title) => (
															<Inline
																key={title.title}
																as="span"
																rounded="md"
																paddingX={"2"}
																paddingY={"1"}
																maxWidth={"sm"}
																backgroundColor={
																	title.type === "transport_service"
																		? "warning"
																		: "success"
																}
																gap="1"
															>
																{title.type === "transport_service" ? (
																	<Icons.Taxi />
																) : (
																	<Icons.Ticket />
																)}
																<ServiceTitle title={title.title} />
															</Inline>
														))}
													</Stack>
												</Inline>
												<Box>
													{expandedDays.includes(dayService.date) ? (
														<Icons.MinusCircle color="muted" />
													) : null}
												</Box>
											</Inline>
											{expandedDays.includes(dayService.date) ? (
												<Stack gap="2" fontSize="xs" padding={"1"}>
													{(dayService.services || []).map((s) => {
														if (s.cab_schedule) {
															const description = getDescriptionFromItinerary(
																s.cab_schedule.transport_service,
																"transport_service",
																dayService.date
															)
															return (
																<Stack
																	key={s.cab_schedule.transport_service.id}
																	gap="1"
																>
																	<Inline
																		gap="2"
																		fontSize="sm"
																		fontWeight="semibold"
																		alignItems="center"
																	>
																		<ServiceBadge type="transport_service" />
																		<ServiceTitle
																			title={
																				s.cab_schedule.transport_service.name
																			}
																			cabs={
																				used_cab_types
																					? undefined
																					: s.cab_schedule.cabs
																			}
																		/>
																	</Inline>
																	<Text>{description}</Text>
																</Stack>
															)
														}
														if (s.travel_activity) {
															const description = getDescriptionFromItinerary(
																s.travel_activity,
																"travel_activity",
																dayService.date
															)
															return (
																<Stack key={s.travel_activity.id} gap="1">
																	<Inline
																		gap="2"
																		fontSize="sm"
																		fontWeight="semibold"
																		alignItems="center"
																	>
																		<ServiceBadge type="travel_activity" />
																		<ServiceTitle
																			title={`${s.travel_activity.activity.name} - ${s.travel_activity.ticket_type?.name || s.travel_activity.ticket_type?.short_name}`}
																		/>
																	</Inline>
																	<Text>{description}</Text>
																</Stack>
															)
														}
														return null
													})}
												</Stack>
											) : null}
										</Stack>
									)
								})
							) : (
								<Box textAlign="center" padding="2">
									<Text color="muted">
										Itinerary not available in this quote
									</Text>
								</Box>
							)}
							{grouped_daywise_services &&
							grouped_daywise_services.length > 8 &&
							!showAllItinerary ? (
								<Box textAlign="center" padding="2">
									<Button size="sm" onClick={() => setShowAllItinerary(true)}>
										Show More
									</Button>
								</Box>
							) : null}
						</Stack>
					</TabContent>

					<TabContent
						active={activeTab === "hotels"}
						id="hotels"
						key="hotels"
						padding={"2"}
					>
						<Stack
							style={{
								maxHeight: "420px",
								minHeight: "250px",
							}}
							overflow="auto"
							gap="2"
						>
							<Stack gap="4">
								{(matchingQuote.trip_quote.quote?.hotels || []).length > 0 ||
								(matchingQuote.trip_quote.options || []).some(
									(option) => (option.quote?.hotels || []).length > 0
								) ? (
									<>
										{/* Primary/First Option */}
										{(matchingQuote.trip_quote.quote?.hotels || []).length >
										0 ? (
											<Stack key="primary" gap="2">
												{(matchingQuote.trip_quote.options || []).length > 0 ? (
													<Text fontSize="sm" fontWeight="semibold">
														Option 1
														{matchingQuote.trip_quote.name
															? ` - ${matchingQuote.trip_quote.name}`
															: ""}
													</Text>
												) : null}
												<HotelTable hotels={groupedPrimaryHotels} />
												{totalPrimaryGroupedHotels.length >
												((matchingQuote.trip_quote.options || []).length > 0
													? INITIAL_ROWS_LIMIT
													: PRIMARY_ONLY_ROWS_LIMIT) ? (
													<Box textAlign="center">
														<Button
															size="sm"
															onClick={() => toggleOption("primary")}
														>
															{expandedOptions["primary"]
																? "Show Less"
																: "Show More"}
														</Button>
													</Box>
												) : null}
											</Stack>
										) : null}
										{/* Additional Options */}
										{(additionalOptions || []).map((option, optionIndex) => {
											const optionHotels = option.quote?.hotels || []
											return optionHotels.length > 0 ? (
												<Stack key={option.id} gap="2">
													<Text fontSize="sm" fontWeight="semibold">
														Option {optionIndex + 1 + 1}
														{option.name ? ` - ${option.name}` : ""}
													</Text>
													<HotelTable hotels={option.groupedHotels} />
													{option.totalGroupedHotels.length >
													INITIAL_ROWS_LIMIT ? (
														<Box textAlign="center">
															<Button
																size="sm"
																onClick={() =>
																	toggleOption(option.id.toString())
																}
															>
																{expandedOptions[option.id.toString()]
																	? "Show Less"
																	: "Show More"}
															</Button>
														</Box>
													) : null}
												</Stack>
											) : null
										})}
									</>
								) : (
									<Box textAlign="center" padding="2">
										<Text color="muted">
											Hotels not available in this quote
										</Text>
									</Box>
								)}
							</Stack>
						</Stack>
					</TabContent>

					<TabContent
						active={activeTab === "details"}
						id="details"
						key="details"
						padding="2"
					>
						<Stack gap="2" fontSize="xs">
							<Inline justifyContent="between">
								<Text>Duration:</Text>
								<Text fontWeight="semibold">{days} Days</Text>
							</Inline>
							<Inline justifyContent="between">
								<Text>Travelers:</Text>
								<Text fontWeight="semibold">
									{joinAttributes(
										no_of_adults ? `${no_of_adults} Adults` : null,
										no_of_children ? `${no_of_children} Children` : null
									)}
								</Text>
							</Inline>

							{used_cab_types && used_cab_types.length > 0 ? (
								<Inline justifyContent={"between"}>
									<Text>Cabs:</Text>

									<Text fontWeight="semibold">
										{formatCabDisplay(used_cab_types)}
									</Text>
								</Inline>
							) : null}
						</Stack>
					</TabContent>
				</Tabs>
			</Box>

			<Box padding="4" paddingTop="0">
				{shouldAllowUseThisQuote ? (
					<ButtonLink
						status="primary"
						level="secondary"
						fullWidth
						to={`${generatePath("/trips/:tripId/new-quote", {
							tripId: trip.id.toString(),
						})}${queryToSearch(
							getBasicInfoForQueryFromTrip(trip, matchingQuote.id)
						)}&using=1`}
						data-testid={`use_quote_${index}`}
					>
						Use this Quote
					</ButtonLink>
				) : (
					<Button
						onClick={tempDisableQuoteActionsMessageAlert}
						status="primary"
						level="secondary"
						fullWidth
					>
						Use this Quote
					</Button>
				)}
			</Box>
		</Box>
	)
}
